import { useMemo } from 'react'
import moment from 'moment'
import useTranslation from 'next-translate/useTranslation'

import useAppSelector from '../../../store/hooks/useAppSelector'
import useGetOrderFormatName from '../../app/hooks/useGetOrderFormatName'
import getFormattedOrderTimeSlot from '../../outlet/utils/getFormattedOrderTimeSlot'
import { outletsSelectedOutletListItemSelector } from '../../outlets/store/outletsSelectors'
import {
  preOrderNewGroupOrderNameSelector,
  preOrderNewGroupOrderSizeSelector,
  preOrderNewOrderDateSelector,
  preOrderNewOrderOrderFormatSelector,
  preOrderNewOrderPanelSelector,
  preOrderNewOrderSelectedOrderMethodSelector,
  preOrderNewOrderTimeSelector,
  preOrderSelectedRestaurantMaxAttendeeLimit,
  preOrderSettingsSelector,
} from '../store/preOrderSelectors'
import {
  NewOrderPanelStep,
  NewOrderPanelStepState,
  PreOrderMethod,
} from '../types'
import isGroupOrderDetailsValid from '../utils/isGroupOrderDetailsValid'
import isCompanyOrderDateInRange from '../utils/isCompanyOrderDateInRange'
import getOutletToday from '../../outlet/utils/getOutletToday'

/** Each step of the new order panel, in its order, with what was chosen */
export default function useNewOrderPanelSteps(): NewOrderPanelStepState[] {
  const { t } = useTranslation('preOrder')
  const getOrderFormatName = useGetOrderFormatName()

  const { stepOrder } = useAppSelector(preOrderNewOrderPanelSelector)
  const outlet = useAppSelector(outletsSelectedOutletListItemSelector)
  const orderFormat = useAppSelector(preOrderNewOrderOrderFormatSelector)
  const orderDate = useAppSelector(preOrderNewOrderDateSelector)
  const orderTime = useAppSelector(preOrderNewOrderTimeSelector)
  const orderMethod = useAppSelector(
    preOrderNewOrderSelectedOrderMethodSelector,
  )
  const groupOrderName = useAppSelector(preOrderNewGroupOrderNameSelector)
  const groupOrderSize = useAppSelector(preOrderNewGroupOrderSizeSelector)
  const maxGroupSize = useAppSelector(
    preOrderSelectedRestaurantMaxAttendeeLimit,
  )
  const preOrderSettings = useAppSelector(preOrderSettingsSelector)

  return useMemo(() => {
    const isGroup = orderMethod === PreOrderMethod.Group

    const stateByStep: Record<
      NewOrderPanelStep,
      Omit<NewOrderPanelStepState, 'step'>
    > = {
      [NewOrderPanelStep.Restaurant]: {
        value: outlet?.name ?? null,
        isComplete: !!outlet,
      },
      [NewOrderPanelStep.Format]: {
        value: orderFormat != null ? getOrderFormatName(orderFormat) : null,
        isComplete: orderFormat != null,
      },
      [NewOrderPanelStep.Date]: {
        value: orderDate ? moment(orderDate).format('ddd, ll') : null,
        // A day outside the calendar, e.g. picked in the overview, is asked
        // again. Reading today is safe: the panel only mounts after a click.
        isComplete:
          !!orderDate &&
          isCompanyOrderDateInRange(
            orderDate,
            preOrderSettings,
            getOutletToday(outlet),
          ),
      },
      [NewOrderPanelStep.Time]: {
        value: orderTime ? getFormattedOrderTimeSlot(orderTime) : null,
        isComplete: !!orderTime,
      },
      [NewOrderPanelStep.OrderType]: {
        value: isGroup
          ? t('newOrderPanel.type.group')
          : t('newOrderPanel.type.order'),
        isComplete:
          !isGroup ||
          isGroupOrderDetailsValid(
            groupOrderName,
            groupOrderSize,
            maxGroupSize,
          ),
      },
    }

    return stepOrder.map(step => ({ step, ...stateByStep[step] }))
  }, [
    getOrderFormatName,
    groupOrderName,
    groupOrderSize,
    maxGroupSize,
    orderDate,
    orderFormat,
    orderMethod,
    orderTime,
    outlet,
    preOrderSettings,
    stepOrder,
    t,
  ])
}
